import * as React from 'react';
import * as cx from 'classnames';

import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';

import {
    PortCallProfile,
    IssuesViewModel,
    DeletePortCallViewModel,
    CancelPortCallViewModel,
    ManageCoordinationsViewModel,
    ReserveByLengthViewModel,
    ExternalVesselSearchViewModel,
    TabItinerary,
    TabVessel,
    TabPeople,
    TabLog,
} from './model';
import { ActionsMenuButton } from 'portcall/actionsmenubutton/view';

import { DomainRights, PortCallRights } from 'server/enums/Rights';
import { TopicCardStatus } from 'server/enums/TopicCardStatus';

import { PersonProfilePopupView } from 'portcall/personprofilepopup/view';
import { PortCallLogView } from 'portcall/portcalllog/view';
import { LocalVesselSearchView } from 'portcall/localvesselsearch/view';
import { ActionLoadingIndicator } from 'core/actionloadingindicator/view';
import { ErrorModal } from 'core/validationerror/view';
import { NullableDatePicker } from 'portcall/datetimepicker/view';
import { ToggleableMeasurementView } from 'portcall/toggleablemeasurement/view';
import { DateTimeEditPopupView } from 'portcall/datetimeeditpopup/view';
import { PCDismissible } from 'portcall/pcdismissible/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PortCallHeaderView } from './portcallheaderview';
import { IssueDescription } from 'portcall/issues/view';
import { NewVesselView } from 'portcall/newvessel/view';
import { TopicCardListItemView } from 'portcall/topiccardlistitem/view';
import { UserSelectorView } from 'portcall/userselector/view';
import { ToggleButton } from 'portcall/togglebutton/view';
import { WartsilaEnrichmentsSummaryCardView } from 'wartsila/wartsilaenrichments/summaryCardView';
import { PortCallScheduleView } from 'portcall/portcallschedule/view';
import { SelectCoordinationsView } from 'portcall/selectcoordinations/view';
import { PCTabStripView, TitledTabStripViewController, TitledTabContentsController, PCTabContents } from 'portcall/pctabstrip/view';
import { TitledTab } from 'portcall/pctabstrip/model';
import { PrintPortCallReportLink, PortCallReportView } from './portcallreportview';

const ItineraryTabContents = SuperCritical(({model} : { model : PortCallProfile }) => {
    return (
        <div className="pcprofile-tab-contents itinerary-tab-contents">
            <PortCallScheduleView model={model.Schedule} />
            {/* TODO: Do we display cancelled reservations here somewhere? They don't show up on the metro map,
                so as is the only indication in the UI that they ever existed would be in the log. Which might
                be OK? */}
        </div>
    );
});

const PeopleTabContents = SuperCritical(({model} : { model : PortCallProfile }) =>
    <div className="pcprofile-tab-contents people-tab-contents">
        <div className="back-tab-header">
            <i className="back-tab-header-icon fa fa-group"></i>
            <span className="back-tab-title">People</span>
        </div>
        <div>
            <div className="agent-section">
                <div className="title">Attending Agent</div>
                <div>
                    <UserSelectorView
                        model={model.People.AgentSelector}
                        className="agent-typeahead"
                        disabled={(model.Rights() & PortCallRights.Edit) === 0}
                    />
                </div>
            </div>
            {(model.Rights() & PortCallRights.Edit) === 0 ? null :
                <div className="add-editors-section">
                    <div className="title">Editors</div>
                    <div>
                        <UserSelectorView className="editors-typeahead" placeholder="Invite Editor..." model={model.People.InvitedEditorSelector} />{" "}
                        <button type="button" className="pc-button" onClick={() => model.People.InviteEditor()}>add</button>
                    </div>
                </div>
            }
        </div>
        {[{ editors: model.People.AgencyEditors,              title: 'Agency',               showDelete: false }, 
            { editors: model.People.SchedulingAuthorityEditors, title: 'Scheduling Authority', showDelete: false }, 
            { editors: model.People.InvitedEditors,             title: 'Invited Editors',      showDelete: true }].map(e => 
            <div className="section" key={e.title}>
                <div className="title">{e.title}</div>
                <div className="spacer"></div>
                <ul>
                    {e.editors().map(p => 
                        <li className="people-column" key={p.Id()}>
                            <img className="thumb-img" src={p.Image.ThumbUrl()} />
                            <div className="person">
                                <a href="" className="pc-text-fixedwidth person-name" onClick={noDefault(() => p.ToggleProfile())}>{p.Name()}</a>
                                <div className="pc-text-smalllabel pc-text-fixedwidth person-org">{p.Organization() ? p.Organization()!.Name() : ''}</div>
                            </div>
                            {!e.showDelete || (model.Rights() & PortCallRights.Edit) === 0 ? null : <a href="" className="delete-btn pc-close-icon" onClick={noDefault(() => p.DeleteInvitedEditorShare())}></a>}
                            <PersonProfilePopupView modelContainer={p.ProfilePopup} boundingContainer=".face.flipped" boundingPadding={40} />
                        </li>
                    )}
                </ul>
            </div>
        )}
    </div>
);

const VesselTabContents = SuperCritical(({model} : { model : PortCallProfile }) => {
    const pc = model.PortCall,
        v = model.Vessel(),
        dos = v && v.DOS;

    return (
        <div className="pcprofile-tab-contents vessel-tab-contents">
            <div className="back-tab-header">
                <i className="back-tab-header-icon icon-ship"></i>
                <span className="back-tab-title">Vessel</span>
            </div>

            <div className="vessel-details">
                <div className="vessel-actions">
                    <div hidden={(model.Rights() & PortCallRights.Edit) === 0}>
                        <LocalVesselSearchView model={model.VesselSearch} />
                        <div className="addl-links">
                            <div className="addl-right">
                                <div hidden={(model.DomainRights() & DomainRights.AddVessel) === 0}>
                                    <span className="pc-text-smalllabel">Can't find the right vessel? </span>
                                    <a href="" onClick={noDefault(() => model.ShowExternalVesselSearchModal(true))}>Add it.</a>
                                    <div className="reservation-options-spacing"></div>
                                </div>
                                {v === null && <span className="pc-text-smalllabel">Not the correct size? </span> }
                                {v === null && <a href="" onClick={noDefault(e => model.ReserveByLengthModal.Show(true))}>Edit the length.</a> }
                            </div>
                        </div>
                    </div>
                </div>
                <div className="vessel-info">
                    <div className="particulars">
                        <span className="particulars-title">Vessel Particulars</span>
                    </div>
                    <div className="name">
                        <span className="value">{pc.DisplayVesselIdentifier()}</span>
                    </div>
                    <div className="air-draft">
                        <span className="pc-text-smalllabel lbl">Air Draft</span>
                        <ToggleableMeasurementView model={pc.ToggleableAirDraft} disabled={(model.Rights() & PortCallRights.Edit) === 0} />
                    </div>
                    <div className="gross-tonnage">
                        <span className="pc-text-smalllabel lbl">Gross Tonnage</span>
                        <span className="pc-text-smalltext value">{v && v.GrossTonnage.Num()}</span>
                    </div>
                    <div className="length">
                        <span className="pc-text-smalllabel lbl">Length Overall</span>
                        <span className="pc-text-smalltext value">
                            {v 
                                ? <ToggleableMeasurementView model={v.ToggleableLength} textonly={true} />
                                : <ToggleableMeasurementView model={pc.ToggleableVesselLength} textonly={true} />}
                        </span>
                    </div>
                    <div className="beam">
                        <span className="pc-text-smalllabel lbl">Beam</span>
                        <span className="pc-text-smalltext value">
                            {v ? <ToggleableMeasurementView model={v.ToggleableBeam} textonly={true} /> : null}
                        </span>
                    </div>
                    <div className="deep-draft">
                        <span className="pc-text-smalllabel lbl">Deep Draft</span>
                        <ToggleableMeasurementView model={pc.ToggleableDeepDraft} disabled={(model.Rights() & PortCallRights.Edit) === 0} />
                    </div>
                    <div className="identifiers">
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">IMO</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.IMO()}</span>
                        </div>
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">MMSID</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.MMSID()}</span>
                        </div>
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">Call Sign</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.CallSign()}</span>
                        </div>
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">VIN</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.VIN()}</span>
                        </div>
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">Vessel Type</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.VesselType()}</span>
                        </div>
                        <div className="identifier-row">
                            <span className="identifier-lbl pc-text-smalllabel">Flag</span>
                            <span className="identifier-value pc-text-smalltext pc-text-fixedwidth">{v && v.Flag() && v.Flag()!.Name()}</span>
                        </div>
                    </div>
                    <div className="uomselector uom">
                        <ToggleButton checked={model.ToggleableUomIsFeet} onLabel="Feet" offLabel="Meters" />
                    </div>
                </div>
            </div>

            <div className="ports-and-dos">
                {dos ? <div className="dos">
                    <span className="particulars-title">Declaration of Security</span>
                    <span className="pc-text-tinylabel dos-desc">Issued to validate the vessel.</span>
                    <div className="dos-details">
                        <div className="dos-row">
                            <div className="pc-text-smalllabel dos-label">Effective</div>
                            <NullableDatePicker value={dos.EffectiveDate} disabled={(model.Rights() & PortCallRights.Edit) === 0} localization={model.LocalizationContext} />
                        </div>
                        <div className="dos-row">
                            <div className="pc-text-smalllabel dos-label">Expires</div>
                            <NullableDatePicker value={dos.ExpirationDate} disabled={(model.Rights() & PortCallRights.Edit) === 0} localization={model.LocalizationContext} />
                        </div>
                        <div className="dos-row">
                            <div className="pc-text-smalllabel dos-label">Issued by</div>
                            <input type="text" className="issuer" value={dos.IssuedBy() || ''} disabled={(model.Rights() & PortCallRights.Edit) === 0} onChange={e => dos.IssuedBy(e.currentTarget.value)} />
                        </div>
                    </div>
                </div> : null}
            </div> 
        </div>
    );
});

const LogTabContents = SuperCritical(({ model } : { model : PortCallProfile }) =>
    <div className="pcprofile-tab-contents log-tab-contents">
        <PortCallLogView model={model.PortCallLog} />
    </div>
);

const DeletePortCallModal = SuperCritical(({model} : { model: DeletePortCallViewModel }) => 
    <PCModal className="deleteportcall" shown={model.Show} onBackDropClick={() => model?.Show(false)}>
        <div className="modal-content">
            <div className="modal-body clearfix">
                <h2 className="modal-title">Delete Port Call</h2>
                { !model.ActionSuccess() 
                ? <div>
                        <ActionLoadingIndicator loading={() => !!model.Action() && !model.ActionSuccess()} />
                        <div>
                            Are you sure you want to delete this PortCall?
                        </div>
                        <div className="action-btns">
                            <button className="close pc-modal-close close-btn" onClick={() => model.Show(false)}>Cancel</button>
                            <button type="button" className="pc-button" onClick={model.Delete}>Delete</button>
                        </div>
                    </div>
                    : <div>
                        <div className="confirmation-message">This PortCall was successfully deleted.</div>
                        <div>
                            <button type="button" className="pc-button" onClick={model.DoneDeleting}>OK</button>
                        </div>
                    </div>}
            </div>
        </div>
    </PCModal>
);

const CancelPortCallModal = SuperCritical(({ model } : { model : CancelPortCallViewModel }) =>
    <PCModal className="cancelportcall" shown={model.Show} onBackDropClick={() => model?.Show(false)}>
        <div className="modal-content">
            <div className="modal-body clearfix">
                <h2 className="modal-title">Cancel PortCall</h2>
                {!model.Success() ?
                    <div>
                        <ActionLoadingIndicator loading={model.Working} />
                        <div>
                            Are you sure you want to cancel this PortCall?
                        </div>
                        <div className="remarks-shell">
                            <textarea
                                className="remarks"
                                value={model.Req.Remarks() ?? ''}
                                onChange={e => model.Req.Remarks(e.currentTarget.value || null)}
                                placeholder="These remarks will be sent to editors on this PortCall."
                                rows={3}
                            />
                        </div>
                        <div className="action-btns">
                            <button className="close pc-modal-close close-btn" onClick={() => model.Show(false)}>Close</button>
                            <button type="button" className="pc-button" onClick={model.Cancel}>Cancel</button>
                        </div>
                    </div> :
                    <div>
                        <div className="confirmation-message">This PortCall was successfully cancelled.</div>
                        <div>
                            <button type="button" className="pc-button" onClick={() => model.Show(false)}>OK</button>
                        </div>
                    </div>
                }
            </div>
        </div>
    </PCModal>
);

const ReserveByLengthModal = SuperCritical(({ model } : { model : ReserveByLengthViewModel }) =>
    <PCModal className="reservebylength-modal" shown={model.Show} onBackDropClick={() => model?.Show(false)}>
        <div className="reserve-berth-modal modal-content pc-card pc-card-pcprofile profile-body clearfix">
            <div className="pc-card-header pc-card-title">
                Edit the Vessel Length
            </div>
            <div className="modal-body details">
                <div className="vessel-details">
                    <div className="pc-text-smalllabel manual-description">
                        Enter the approximate length.
                    </div>
                    <div className="vessel-info reserve-by-length">
                        <div className="particulars">
                            <span className="particulars-title">Vessel Particulars </span>
                        </div>
                        <div className="name">
                            <span className="pc-text-smalllabel lbl">Name </span>
                        </div>
                        <div className="air-draft">
                            <span className="pc-text-smalllabel lbl">Air Draft </span>
                            <ToggleableMeasurementView model={model.ToggleableAirDraft} />
                        </div>
                        <div className="gross-tonnage">
                            <span className="pc-text-smalllabel lbl">Gross Tonnage </span>
                        </div>
                        <div className="length">
                            <span className="pc-text-smalllabel lbl">Length Overall </span>
                            <ToggleableMeasurementView model={model.ToggleableNewVesselLength} />
                        </div>
                        <div className="beam">
                            <span className="pc-text-smalllabel lbl">Beam </span><br/>
                        </div>
                        <div className="deep-draft">
                            <span className="pc-text-smalllabel lbl">Deep Draft </span>
                            <ToggleableMeasurementView model={model.ToggleableDeepDraft} />
                        </div>
                        <div className="identifiers">
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">IMO</span>
                            </div>
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">MMSID</span>
                            </div>
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">Call Sign</span>
                            </div>
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">VIN</span>
                            </div>
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">Vessel Type</span>
                            </div>
                            <div className="identifier-row">
                                <span className="identifier-lbl pc-text-smalllabel">Flag</span>
                            </div>
                        </div>
                        <div className="uomselector uom">
                            <ToggleButton onLabel="Feet" offLabel="Meters" checked={model.NewVesselIsFeet} />
                        </div>
                    </div>
                </div>
                <div className="buttons">
                    <a href="" className="cancel-link" onClick={noDefault(() => model.Show(false))}>Cancel Changes </a>
                    <input type="button" className="pc-button" value="Change Length" onClick={model.SaveLength} />
                </div>
            </div>
        </div>
    </PCModal>
);

const ExternalVesselSearchModal = SuperCritical(({ model } : { model : () => ExternalVesselSearchViewModel | null }) => {
    const m = model();
    return (
        <PCModal className="servicevesselsearch-modal" shown={() => !!m} onBackDropClick={m?.Cancel}>
            <div className="service-vessel-search-modal modal-content pc-card pc-card-pcprofile profile-body clearfix">
                <div className="pc-card-header-button">
                    {m && <a className="cancel-vessel-search" onClick={m.Cancel}>Cancel</a>}
                </div>
                <div className="pc-card-header pc-card-title">
                    Add a Vessel
                </div>
                <div className="modal-body details">
                    {m && <NewVesselView model={m.NewVessel} />}
                </div>
                <div className="save-button">
                    {m?.ShowSave() ?
                        <button
                            type="button"
                            className="pc-button"
                            onClick={m.Save}
                            disabled={!m.NewVessel.CanSave()}
                        >
                            Save
                        </button> :
                        null
                    }
                </div>
            </div>
        </PCModal>
    );
});

const IssuesModal = SuperCritical(({model} : { model : IssuesViewModel }) =>
    <PCModal shown={model.Show}  onBackDropClick={() => model?.Show(false)}>
        <div className="modal-content pc-card pc-card-pcprofile">
            <div className="pc-card-header-button">
                <a className="cancel-vessel-search" href="" onClick={noDefault(() => model.Show(false))}>Close</a>
            </div>
            <div className="pc-card-header pc-card-title">
                Conflicts
            </div>
            <div className="modal-body">
                {model.Issues().map(issue => 
                    <IssueDescription key={issue.Id()} issue={issue} />
                )}
            </div>
        </div>
    </PCModal>
);

const ManageCoordinationsModal = SuperCritical(({ model } : { model : () => ManageCoordinationsViewModel | null }) => {
    const m = model();
    return (
        <PCModal className="manage-coordinations-modal" shown={() => !!m} onBackDropClick={m?.Cancel}>
            <div className="modal-content pc-card pc-card-pcprofile">
                <div className="pc-card-header-button">
                    {m && <a className="close-modal" href="" onClick={noDefault(m.Cancel)}>Close</a>}
                </div>
                <div className="pc-card-header pc-card-title">Manage Coordinations</div>
                {m &&
                    <div className="modal-body">
                        <div className="select-coordinations-shell">
                            <SelectCoordinationsView model={m.SelectCoordinations} />
                        </div>
                        <div className="submit-button-shell">
                            <button
                                className="pc-button submit-button"
                                onClick={noDefault(m.Submit)}
                                disabled={!m.CanSubmit()}
                            >
                                Update
                            </button>
                        </div>
                        {m.Submitting() && <div className="manage-coordinations-loading-indicator" />}
                    </div>
                }
            </div>
        </PCModal>
    );
});

const PortsOfCall = SuperCritical(({ model: m } : { model : PortCallProfile }) =>
    <div className="pc-card pc-card-pcprofile ports-of-call">
        <div className="pc-card-title light-card-title">Last/Next Port of Call</div>
        <div className="ports-details">
            <div className="port-of-call">
                <label className="port-of-call-label">Last Port</label>
                <input
                    className="port-of-call-input"
                    placeholder="Port of Call"
                    value={m.PortCall.LastPortOfCall() ?? ''}
                    onChange={e => m.PortCall.LastPortOfCall(e.currentTarget.value)}
                    disabled={(m.Rights() & PortCallRights.Edit) === 0}
                />
            </div>
            <div className="port-of-call">
                <label className="port-of-call-label">Next Port</label>
                <input
                    className="port-of-call-input"
                    placeholder="Port of Call"
                    value={m.PortCall.NextPortOfCall() ?? ''}
                    onChange={e => m.PortCall.NextPortOfCall(e.currentTarget.value)}
                    disabled={(m.Rights() & PortCallRights.Edit) === 0}
                />
            </div>
        </div>
    </div>
);

const TopicCardsView = SuperCritical(({ model: m } : { model : PortCallProfile }) =>
    <div className="pc-card pc-card-pcprofile topic-cards-container">
        <div className="pc-card-title light-card-title">Topic Cards</div>
        <div className="pc-text-tinylabel">
            Read through these cards and answer them accordingly.
        </div>
        <div>
            <div className="card-count-container answered">
                <div className="card-count">{m.TopicCardModels().length}</div>
                <div className="card-count-label">Answered</div>
            </div>
            <div className="card-count-container confirmed">
                <div className="card-count">{m.TopicCardModels().filter(c => c.Model.Status() === TopicCardStatus.Confirmed).length}</div>
                <div className="card-count-label">Confirmed</div>
            </div>
            <div className="card-count-container denied">
                <div className="card-count">{m.TopicCardModels().filter(c => c.Model.Status() === TopicCardStatus.Denied).length}</div>
                <div className="card-count-label">Denied</div>
            </div>
        </div>
        <div className="card-page-link-container">
            <a href={"#!topiccards?id=" + m.PortCall.Id()}>View all ({m.TopicCardModels().length + m.ApplicableTopicCardTemplatesCount()})</a>
        </div>
        <div className="card-list">
            {m.TopicCardModels().map(c =>
                <TopicCardListItemView model={c.ListItem}
                    key={c.Model.Id()}
                    onClick={c.GoToCard} />
            )}
        </div>
    </div>
);

const ActionsMenu = SuperCritical(({ model } : { model : PortCallProfile }) => model.ShowActionsMenu() ?
    <div className="pc-actions">
        <PCDismissible className="pc-actions-menu pc-card" onDismiss={() => model.ShowActionsMenu(false)}>
            <div className="action-item">
                <div>
                    <a href={model.BerthTimelineUrl()}>View on Berths</a>
                </div>
                <div className="pc-action-item-descr">
                    View this PortCall on the Berths Timeline.
                </div>
            </div>
            <div className="action-item">
                <div>
                    <a href="" onClick={noDefault(model.People.WatchPortCall)}>{model.People.IsWatching() ? 'Stop Watching PortCall' : 'Watch this PortCall'}</a>
                </div>
                <div className="pc-action-item-descr">
                    Receive notifications and emails on this PortCall's status and changes.
                </div>
            </div>
            <div className="action-item">
                <div>
                    <a href="" onClick={noDefault(() => window.print())}>Print Itinerary</a>
                </div>
                <div className="pc-action-item-descr">
                    Print off the PortCall Itinerary as it appears on this page.
                </div>
            </div>
            <PrintPortCallReportLink model={model.PortCallReport} />
            <div className="action-item" hidden={(model.Rights() & PortCallRights.Cancel) === 0 || model.PortCall.Cancelled()}>
                <div>
                    <a href="" onClick={noDefault(e => model.CancelPortCallModal.Show(true))}>Cancel PortCall</a>
                </div>
                <div className="pc-action-item-descr">
                    Remove this PortCall and all associated reservations from the schedule. This will remain on your profile as canceled.
                </div>
            </div>
            <div className="pc-admin-separator pc-card-admin-title" hidden={(model.Rights() & PortCallRights.Delete) === 0 && (model.Rights() & PortCallRights.EditUnlistedOverride) === 0}>
                <span>Admin</span><div className="pc-admin-line"></div>
            </div>
            <div className="action-item" hidden={(model.Rights() & PortCallRights.Delete) === 0}>
                <div>
                    <a href="" onClick={noDefault(() => model.DeletePortCallModal.Show(true))}>Delete PortCall</a>
                </div>
                <div className="pc-action-item-descr">
                    Permanently remove this PortCall and all associated reservations from the system. This cannot be undone.
                </div>
            </div>
            <div className="action-item" hidden={(model.Rights() & PortCallRights.EditUnlistedOverride) === 0}>
                <div>
                    <a href="" onClick={noDefault(model.PortCall.ToggleUnlistedOverride)}>
                                {model.PortCall.Unlisted() ? 'List PortCall' : 'Unlist PortCall'}
                            </a>
                </div>
                <div className="pc-action-item-descr">
                    Unlisted PortCalls have limited visibility across the site.
                </div>
            </div>
        </PCDismissible>
    </div>: null
);

// Keep in sync w/ permissions etc. checks in TabStripActionsMenu below.
function HasAnyTabStripActions(m : PortCallProfile) {
    return (m.Rights() & PortCallRights.Edit) !== 0;
}

const TabStripActionsMenu = SuperCritical(({ model: m } : { model : PortCallProfile }) =>
    m.ShowTabStripActionsMenu() ?
        <div className="pc-actions">
            <PCDismissible className="pc-actions-menu pc-card tab-strip-actions-menu" onDismiss={() => m.ShowTabStripActionsMenu(false)}>
                {/* TODO: Add Shift */}
                {(m.Rights() & PortCallRights.Edit) !== 0 &&
                    <div className="action-item">
                        <a href="" onClick={noDefault(m.TabStripMenuAction(() => m.ShowManageCoordinationsModal(true)))}>
                            Manage Coordinations
                        </a>
                    </div>
                }
            </PCDismissible>
        </div> :
        null
);

class PortCallProfileTabContentsController extends TitledTabContentsController {
    constructor(readonly model : PortCallProfile) { super(); }

    renderTabContents(tab : TitledTab) {
        if      (tab === TabItinerary) return <ItineraryTabContents model={this.model} />;
        else if (tab === TabVessel   ) return <VesselTabContents model={this.model} />;
        else if (tab === TabPeople   ) return <PeopleTabContents model={this.model} />;
        else if (tab === TabLog      ) return <LogTabContents model={this.model} />;
        else throw new Error(`Unknown tab with title '${tab.Title}'`);
    }
}

export const PortCallProfileView = SuperCritical(({model} : { model : PortCallProfile }) =>
    <div className={cx(
        "portcall-profile pc-page-fixedwidth", {
            "allow-full-page-take-over": model.ShowPortCallReport()
        }
    )}>
        <div className="pc-actions-links-header">
            <a className="pc-back-link" onClick={noDefault(model.GoBack)}>&lt;&lt; Back</a>
            <ActionsMenuButton onClick={model.ToggleActionsMenu} />
            <ActionsMenu model={model} />
        </div>
        <PortCallHeaderView
            portcall={model.PortCall}
            canEdit={() => (model.Rights() & PortCallRights.Edit) !== 0}
            issues={model.AllIssues}
            onIssueBadgeClick={() => model.IssuesModal.Show(true)}
        />
        <div className="page-columns">
            <div className="left-column">
                <PCTabStripView
                    model={model.TabStrip}
                    ctrl={new TitledTabStripViewController()}
                    extraContents={() =>
                        HasAnyTabStripActions(model) ?
                            <span className="tab-strip-actions-shell">
                                <a
                                    href=""
                                    className="fa fa-bars tab-strip-actions-menu-btn"
                                    onClick={noDefault(model.ToggleTabStripActionsMenu)}
                                />
                                <TabStripActionsMenu model={model} />
                            </span> :
                            null
                    }
                />
                <PCTabContents
                    model={model.TabStrip}
                    ctrl={new PortCallProfileTabContentsController(model)}
                />
            </div>
            <div className="right-column">
                <WartsilaEnrichmentsSummaryCardView model={model.WartsilaEnrichments} extraClassName="pc-card-pcprofile" />
                <PortsOfCall model={model} />
                <TopicCardsView model={model} />
            </div>
        </div>

        <IssuesModal model={model.IssuesModal} />

        <DeletePortCallModal model={model.DeletePortCallModal} />

        <CancelPortCallModal model={model.CancelPortCallModal} />

        <ReserveByLengthModal model={model.ReserveByLengthModal} />

        <ExternalVesselSearchModal model={model.ExternalVesselSearchModal} />

        <ManageCoordinationsModal model={model.ManageCoordinationsModal} />

        <DateTimeEditPopupView
            modelContainer={model.PilotTimeEditPopup}
            localization={model.LocalizationContext}
        />

        <PortCallReportView model={model.PortCallReport} />

        <ErrorModal errors={model.AgentRestrictions} shown={model.ShowAgentRestrictions} title="There are issues with your PortCall" />
    </div>
);



// WEBPACK FOOTER //
// ./apps/portcall/portcallprofile/view.tsx